import { useEffect, useId, useRef, useState } from 'react';
import { Modal } from '../common/Modal';
import { Button } from '../common/Button';
import { useDownloadTrainingEvidencePdf } from '../../hooks/useTrainings';
import type { EvidenceVariant, TrainingEvidencePdfParams } from '../../services/api/training';
import { DateField } from '../ui/DateField';

export interface EvidenceCourseOption {
  slug: string;
  title: string;
  /** True when every active requirement for the course is non-mandatory. */
  optional?: boolean;
}

export interface EvidenceEmployeeTypeOption {
  id: number;
  name: string | null;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  courses: EvidenceCourseOption[];
  employeeTypes: EvidenceEmployeeTypeOption[];
}

const VARIANTS: {
  value: EvidenceVariant;
  title: string;
  description: string;
  confidential?: boolean;
}[] = [
  {
    value: 'customer',
    title: 'Customer proof',
    description:
      'Aggregate numbers only. No names, e-mails or per-person scores. For customer questionnaires and DPAs.',
  },
  {
    value: 'audit',
    title: 'Internal audit',
    description:
      'Adds a per-person list with names, status and scores. Confidential — internal and auditor use only.',
    confidential: true,
  },
];

/** Today's calendar date in Europe/Prague (the backend validates against it). */
function todayIso(): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Prague',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}

/** The date one year before `iso` (YYYY-MM-DD); 29 Feb falls back to 28 Feb. */
export function oneYearBefore(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number) as [number, number, number];
  const lastDay = new Date(Date.UTC(y - 1, m, 0)).getUTCDate();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${y - 1}-${pad(m)}-${pad(Math.min(d, lastDay))}`;
}

const FIELD =
  'block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:bg-gray-50';

export function TrainingEvidenceExportDialog({ isOpen, onClose, courses, employeeTypes }: Props) {
  const uid = useId();
  const download = useDownloadTrainingEvidencePdf();
  const { reset: resetMutation } = download;

  const [variant, setVariant] = useState<EvidenceVariant>('customer');
  const [slugs, setSlugs] = useState<string[]>([]);
  const [includeOptional, setIncludeOptional] = useState(false);
  const [periodFrom, setPeriodFrom] = useState(() => oneYearBefore(todayIso()));
  const [periodTo, setPeriodTo] = useState(() => todayIso());
  const [employeeTypeId, setEmployeeTypeId] = useState('');
  const [language, setLanguage] = useState<'en' | 'cs'>('en');

  // Fresh defaults every time the dialog opens.
  useEffect(() => {
    if (!isOpen) return;
    setVariant('customer');
    setSlugs([]);
    setIncludeOptional(false);
    const today = todayIso();
    setPeriodFrom(oneYearBefore(today));
    setPeriodTo(today);
    setEmployeeTypeId('');
    setLanguage('en');
    resetMutation();
  }, [isOpen, resetMutation]);

  const variantRefs = useRef<Record<EvidenceVariant, HTMLButtonElement | null>>({
    customer: null,
    audit: null,
  });
  const uniqueCourses = Array.from(new Map(courses.map((c) => [c.slug, c])).values());

  const toggleSlug = (slug: string) =>
    setSlugs((cur) => (cur.includes(slug) ? cur.filter((s) => s !== slug) : [...cur, slug]));

  const periodInvalid = periodFrom !== '' && periodTo !== '' && periodFrom > periodTo;

  const submit = () => {
    if (periodInvalid) return;
    const params: TrainingEvidencePdfParams = { variant, language, includeOptional };
    if (slugs.length > 0) params.courseSlugs = slugs;
    if (periodFrom) params.periodFrom = periodFrom;
    if (periodTo) params.periodTo = periodTo;
    if (variant === 'audit' && employeeTypeId) params.employeeTypeId = Number(employeeTypeId);
    download.mutate(params, { onSuccess: onClose });
  };

  const onVariantKeyDown = (e: React.KeyboardEvent) => {
    const keys = ['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp'];
    if (!keys.includes(e.key)) return;
    e.preventDefault();
    const next: EvidenceVariant = variant === 'customer' ? 'audit' : 'customer';
    setVariant(next);
    variantRefs.current[next]?.focus();
  };

  const errorMessage = download.error ? download.error.message : null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Export training evidence (PDF)"
      size="2xl"
      footer={
        <div className="flex justify-end gap-3">
          <Button variant="secondary" onClick={onClose} disabled={download.isPending}>
            Cancel
          </Button>
          <Button onClick={submit} isLoading={download.isPending} disabled={periodInvalid}>
            Download PDF
          </Button>
        </div>
      }
    >
      <div className="space-y-5">
        <div
          role="radiogroup"
          aria-label="Report type"
          className="grid gap-3 sm:grid-cols-2"
          onKeyDown={onVariantKeyDown}
        >
          {VARIANTS.map((v) => {
            const selected = variant === v.value;
            return (
              <button
                key={v.value}
                ref={(el) => {
                  variantRefs.current[v.value] = el;
                }}
                type="button"
                role="radio"
                aria-checked={selected}
                tabIndex={selected ? 0 : -1}
                onClick={() => setVariant(v.value)}
                className={`rounded-lg border p-3 text-left focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                  selected
                    ? 'border-primary-500 bg-primary-50'
                    : 'border-gray-300 bg-white hover:bg-gray-50'
                }`}
              >
                <span className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                  {v.title}
                  {v.confidential && (
                    <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
                      Confidential
                    </span>
                  )}
                </span>
                <span className="mt-1 block text-xs text-gray-600">{v.description}</span>
              </button>
            );
          })}
        </div>

        <fieldset>
          <legend className="text-sm font-medium text-gray-700">Courses</legend>
          <p className="mb-2 text-xs text-gray-500">
            {slugs.length === 0
              ? includeOptional
                ? 'None selected: all active courses are included (mandatory and optional).'
                : 'None selected: all active mandatory courses are included.'
              : `${slugs.length} selected: only these courses are included.`}
          </p>
          {uniqueCourses.length === 0 ? (
            <p className="rounded-lg border border-gray-200 p-2 text-xs text-gray-500">
              No active training requirements yet. Add a requirement to include its course in the
              report.
            </p>
          ) : (
            <div className="max-h-40 space-y-1 overflow-y-auto rounded-lg border border-gray-200 p-2">
              {uniqueCourses.map((c) => (
                <label key={c.slug} className="flex items-center gap-2 text-sm text-gray-800">
                  <input
                    type="checkbox"
                    checked={slugs.includes(c.slug)}
                    onChange={() => toggleSlug(c.slug)}
                    className="h-4 w-4 rounded border-gray-300 text-primary-600"
                  />
                  {c.title}
                  {c.optional && (
                    <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                      optional
                    </span>
                  )}
                </label>
              ))}
            </div>
          )}
        </fieldset>

        <label className="flex items-center gap-2 text-sm text-gray-800">
          <input
            type="checkbox"
            checked={includeOptional}
            onChange={(e) => setIncludeOptional(e.target.checked)}
            className="h-4 w-4 rounded border-gray-300 text-primary-600"
          />
          Include optional courses
        </label>

        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label
              htmlFor={`${uid}-period`}
              className="mb-1 block text-sm font-medium text-gray-700"
            >
              Period from
            </label>
            <DateField
              id={`${uid}-period`}
              max={periodTo || todayIso()}
              value={periodFrom}
              onChange={setPeriodFrom}
              aria-describedby={`${uid}-period-help`}
              className={FIELD}
            />
          </div>
          <div>
            <label
              htmlFor={`${uid}-period-to`}
              className="mb-1 block text-sm font-medium text-gray-700"
            >
              Period to
            </label>
            <DateField
              id={`${uid}-period-to`}
              min={periodFrom || undefined}
              max={todayIso()}
              value={periodTo}
              onChange={setPeriodTo}
              aria-describedby={`${uid}-period-help`}
              aria-invalid={periodInvalid}
              className={FIELD}
            />
          </div>
          <p id={`${uid}-period-help`} className="text-xs text-gray-500 sm:col-span-2">
            Counts completions between these dates. Compliance status is always as of today.
          </p>
          {periodInvalid && (
            <p role="alert" className="text-xs text-red-700 sm:col-span-2">
              Period from must not be after Period to.
            </p>
          )}
        </div>

        {variant === 'audit' && (
          <div>
            <label htmlFor={`${uid}-type`} className="mb-1 block text-sm font-medium text-gray-700">
              Employee type
            </label>
            <select
              id={`${uid}-type`}
              value={employeeTypeId}
              onChange={(e) => setEmployeeTypeId(e.target.value)}
              className={FIELD}
            >
              <option value="">All employee types</option>
              {employeeTypes.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name ?? `#${t.id}`}
                </option>
              ))}
            </select>
          </div>
        )}

        <div>
          <label htmlFor={`${uid}-lang`} className="mb-1 block text-sm font-medium text-gray-700">
            Language
          </label>
          <select
            id={`${uid}-lang`}
            value={language}
            onChange={(e) => setLanguage(e.target.value as 'en' | 'cs')}
            className={FIELD}
          >
            <option value="en">English</option>
            <option value="cs">Čeština</option>
          </select>
        </div>

        {errorMessage && (
          <p role="alert" className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
            {errorMessage}
          </p>
        )}
      </div>
    </Modal>
  );
}
